/* Flavourful Bites homepage interactions. Each section is a custom element, so it
   re-initialises cleanly when the theme editor reloads a section. */
(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function formatMoney(cents, symbol) {
    const value = cents / 100;
    const digits = Number.isInteger(value) ? 0 : 2;
    return symbol + value.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: 2 });
  }

  /* Hero carousel: cycles slides, rotates the pack line-up, pauses on hover/focus. */
  class FbHero extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.packs = Array.from(this.querySelectorAll('[data-pack]'));
      this.nuts = Array.from(this.querySelectorAll('[data-nuts]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.counter = this.querySelector('[data-counter]');
      this.count = this.slides.length;
      this.index = 0;
      this.paused = false;
      if (!this.count) return;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
      this.addEventListener('mouseenter', () => (this.paused = true));
      this.addEventListener('mouseleave', () => (this.paused = false));
      this.addEventListener('focusin', () => (this.paused = true));
      this.addEventListener('focusout', () => (this.paused = false));
      this.go(0);
      const interval = parseInt(this.dataset.interval, 10) || 3000;
      if (this.dataset.autoplay === 'true' && this.count > 1 && !reduceMotion) {
        this.timer = setInterval(() => {
          if (!this.paused && !document.hidden) this.go(this.index + 1);
        }, interval);
      }
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    go(i) {
      const n = this.count;
      this.index = ((i % n) + n) % n;
      const s = this.slides[this.index];
      this.style.setProperty('--hero-bg', s.dataset.bg);
      this.style.setProperty('--hero-ring', s.dataset.ring);
      this.style.setProperty('--hero-dash', s.dataset.dash);
      this.style.setProperty('--hero-ink', s.dataset.ink);
      this.style.setProperty('--hero-hi', s.dataset.hi);
      this.style.setProperty('--hero-body', s.dataset.body);
      this.style.setProperty('--hero-chip-bg', s.dataset.chipBg);
      this.style.setProperty('--hero-chip-fg', s.dataset.chipFg);
      this.style.setProperty('--hero-cta-bg', s.dataset.ctaBg);
      this.style.setProperty('--hero-cta-fg', s.dataset.ctaFg);
      this.slides.forEach((el, k) => {
        el.classList.toggle('is-active', k === this.index);
        el.setAttribute('aria-hidden', k === this.index ? 'false' : 'true');
        el.toggleAttribute('inert', k !== this.index);
      });
      this.packs.forEach((el, k) => {
        const rel = (k - this.index + n) % n;
        el.classList.toggle('is-front', rel === 0);
        el.classList.toggle('is-right', rel === 1);
        el.classList.toggle('is-left', n > 2 && rel === n - 1);
      });
      this.nuts.forEach((el, k) => el.classList.toggle('is-active', k === this.index));
      this.dots.forEach((dot, k) => dot.setAttribute('aria-selected', k === this.index ? 'true' : 'false'));
      if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0') + ' / ' + String(n).padStart(2, '0');
    }
  }

  /* Product card: size pills pick the variant and update the price. */
  class FbProductCard extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('script[type="application/json"]');
      if (!data) return;
      this.variants = JSON.parse(data.textContent);
      this.symbol = this.dataset.currency || '';
      this.idInput = this.querySelector('input[name="id"]');
      this.now = this.querySelector('[data-price-now]');
      this.was = this.querySelector('[data-price-was]');
      this.button = this.querySelector('[data-add]');
      this.querySelectorAll('[data-variant-radio]').forEach((radio) =>
        radio.addEventListener('change', () => this.select(parseInt(radio.value, 10)))
      );
    }

    select(id) {
      const v = this.variants.find((x) => x.id === id);
      if (!v) return;
      this.idInput.value = v.id;
      this.now.textContent = formatMoney(v.price, this.symbol);
      if (this.was) {
        this.was.hidden = !(v.compare_at_price > v.price);
        this.was.textContent = v.compare_at_price ? formatMoney(v.compare_at_price, this.symbol) : '';
      }
      this.button.disabled = !v.available;
      this.button.textContent = v.available ? this.button.dataset.addLabel : this.button.dataset.soldLabel;
    }
  }

  /* Flavour explorer: tabs switch panels, occasion chips jump to a flavour with a badge. */
  class FbExplorer extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-ftab]'));
      this.panels = Array.from(this.querySelectorAll('[data-fpanel]'));
      this.dots = Array.from(this.querySelectorAll('[data-fdot]'));
      this.chips = Array.from(this.querySelectorAll('[data-occ]'));
      this.tabs.forEach((tab, i) => tab.addEventListener('click', () => this.show(i, null)));
      this.chips.forEach((chip) =>
        chip.addEventListener('click', () => {
          const target = Math.min(parseInt(chip.dataset.target, 10) - 1 || 0, this.panels.length - 1);
          this.show(target, chip);
        })
      );
      this.tabs.forEach((tab, i) =>
        tab.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.show(next, null);
          this.tabs[next].focus();
        })
      );
    }

    show(i, chip) {
      this.tabs.forEach((t, k) => {
        t.setAttribute('aria-selected', k === i ? 'true' : 'false');
        t.tabIndex = k === i ? 0 : -1;
      });
      this.panels.forEach((p, k) => {
        p.hidden = k !== i;
        if (k === i) {
          p.classList.remove('fb-anim-slide');
          void p.offsetWidth;
          p.classList.add('fb-anim-slide');
          const badge = p.querySelector('[data-badge]');
          if (badge) {
            badge.hidden = !chip;
            badge.textContent = chip ? (this.dataset.badgePrefix || 'Perfect for') + ' ' + chip.textContent.trim().toLowerCase() : '';
          }
        }
      });
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      this.chips.forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
    }
  }

  /* Build your box: pick up to N pouches, preview the tiered discount, add all to cart. */
  class FbBoxBuilder extends HTMLElement {
    connectedCallback() {
      this.rows = Array.from(this.querySelectorAll('[data-box-item]'));
      this.slots = Array.from(this.querySelectorAll('[data-slot]'));
      this.max = parseInt(this.dataset.max, 10) || 6;
      this.symbol = this.dataset.currency || '';
      this.tiers = [
        { qty: parseInt(this.dataset.tier2Qty, 10), pct: parseFloat(this.dataset.tier2Pct) },
        { qty: parseInt(this.dataset.tier1Qty, 10), pct: parseFloat(this.dataset.tier1Pct) },
      ];
      this.rows.forEach((row) => {
        row.querySelector('[data-inc]').addEventListener('click', () => this.change(row, 1));
        row.querySelector('[data-dec]').addEventListener('click', () => this.change(row, -1));
      });
      this.querySelector('[data-reset]')?.addEventListener('click', () => {
        this.rows.forEach((row) => (row.dataset.qty = row.dataset.default));
        this.render();
      });
      this.querySelector('[data-add-box]')?.addEventListener('click', () => this.addToCart());
      this.render();
    }

    total() {
      return this.rows.reduce((sum, row) => sum + parseInt(row.dataset.qty, 10), 0);
    }

    change(row, delta) {
      const q = parseInt(row.dataset.qty, 10) + delta;
      if (q < 0 || this.total() + delta > this.max) return;
      row.dataset.qty = q;
      this.render();
    }

    render() {
      const n = this.total();
      let sub = 0;
      const seq = [];
      this.rows.forEach((row) => {
        const q = parseInt(row.dataset.qty, 10);
        row.querySelector('output').textContent = q;
        sub += q * parseInt(row.dataset.price, 10);
        for (let k = 0; k < q; k++) seq.push(row);
      });
      this.slots.forEach((slot, k) => {
        const row = seq[k];
        slot.innerHTML = '';
        slot.style.background = row ? row.dataset.tone : 'transparent';
        slot.style.borderColor = row ? row.dataset.tone : '';
        if (row && row.dataset.thumb) {
          const img = document.createElement('img');
          img.src = row.dataset.thumb;
          img.alt = '';
          img.width = 44;
          img.height = 58;
          slot.appendChild(img);
        }
      });
      const tier = this.tiers.find((t) => t.qty && n >= t.qty);
      const rate = tier ? tier.pct / 100 : 0;
      const disc = Math.round((sub * rate) / 100) * 100;
      const t1 = this.tiers[1];
      const t2 = this.tiers[0];
      let hint;
      if (t1.qty && n < t1.qty) hint = this.dataset.hintMore.replace('[n]', t1.qty - n).replace('[pct]', t1.pct);
      else if (t2.qty && n < t2.qty) hint = this.dataset.hintMore.replace('[n]', t2.qty - n).replace('[pct]', t2.pct);
      else hint = this.dataset.hintMax.replace('[pct]', tier ? tier.pct : 0);
      this.querySelector('[data-count]').textContent = this.dataset.countLabel.replace('[n]', n).replace('[max]', this.max);
      this.querySelector('[data-bar]').style.width = Math.round((n / this.max) * 100) + '%';
      this.querySelector('[data-hint]').textContent = hint;
      this.querySelector('[data-sub]').textContent = formatMoney(sub, this.symbol);
      this.querySelector('[data-disc]').textContent = (disc ? '−' : '') + formatMoney(disc, this.symbol);
      this.querySelector('[data-total]').textContent = formatMoney(sub - disc, this.symbol);
      const add = this.querySelector('[data-add-box]');
      if (add) add.disabled = !this.rows.some((row) => row.dataset.variant && parseInt(row.dataset.qty, 10) > 0);
    }

    async addToCart() {
      const items = this.rows
        .filter((row) => row.dataset.variant && parseInt(row.dataset.qty, 10) > 0)
        .map((row) => ({ id: parseInt(row.dataset.variant, 10), quantity: parseInt(row.dataset.qty, 10) }));
      const error = this.querySelector('[data-error]');
      if (!items.length) return;
      const button = this.querySelector('[data-add-box]');
      button.disabled = true;
      try {
        if (window.fbCart) {
          await window.fbCart.add(items, { trigger: button });
          button.disabled = false;
          return;
        }
        const res = await fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error((await res.json()).description || res.statusText);
        window.location.href = this.dataset.cartUrl || '/cart';
      } catch (e) {
        if (error) {
          error.hidden = false;
          error.textContent = e.message;
        }
        button.disabled = false;
      }
    }
  }

  /* Reviews: filter by tag and page through the cards. */
  class FbReviews extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-review]'));
      this.tabs = Array.from(this.querySelectorAll('[data-filter]'));
      this.prev = this.querySelector('[data-rv-prev]');
      this.next = this.querySelector('[data-rv-next]');
      this.grid = this.querySelector('[data-rv-grid]');
      this.filter = 'all';
      this.page = 0;
      this.tabs.forEach((tab) =>
        tab.addEventListener('click', () => {
          this.filter = tab.dataset.filter;
          this.page = 0;
          this.render(true);
        })
      );
      this.prev?.addEventListener('click', () => {
        this.page--;
        this.render(true);
      });
      this.next?.addEventListener('click', () => {
        this.page++;
        this.render(true);
      });
      this.mq = [window.matchMedia('(min-width: 990px)')];
      this.onResize = () => this.render(false);
      this.mq[0].addEventListener('change', this.onResize);
      this.render(false);
    }

    disconnectedCallback() {
      this.mq?.[0].removeEventListener('change', this.onResize);
    }

    render(animate) {
      const per = this.mq[0].matches ? 3 : 1;
      const list = this.cards.filter((c) => this.filter === 'all' || c.dataset.tag === this.filter);
      const pages = Math.max(1, Math.ceil(list.length / per));
      this.page = Math.max(0, Math.min(this.page, pages - 1));
      const shown = list.slice(this.page * per, this.page * per + per);
      this.cards.forEach((c) => (c.hidden = !shown.includes(c)));
      this.tabs.forEach((t) => t.setAttribute('aria-pressed', t.dataset.filter === this.filter ? 'true' : 'false'));
      if (this.prev) this.prev.disabled = this.page === 0;
      if (this.next) this.next.disabled = this.page >= pages - 1;
      if (animate && this.grid) {
        this.grid.classList.remove('fb-anim-slide');
        void this.grid.offsetWidth;
        this.grid.classList.add('fb-anim-slide');
      }
    }
  }

  const define = (name, cls) => customElements.get(name) || customElements.define(name, cls);
  define('fb-hero', FbHero);
  define('fb-product-card', FbProductCard);
  define('fb-explorer', FbExplorer);
  define('fb-box-builder', FbBoxBuilder);
  define('fb-reviews', FbReviews);
})();
